import { supabase, isConfigured, storePendingMarketingConsent, checkRateLimits, logEvent } from '@rehub/supabase';
import {
  getActiveSubBrand,
  getSubBrand,
  listMySubBrands,
  isFieldLocked,
  type SubBrand,
  type BrandFieldKey,
} from '@rehub/branding';

export { supabase, isConfigured, storePendingMarketingConsent, checkRateLimits, logEvent };

// Stripe billing portal (invoices, payment methods, subscriptions). Shared
// across every ReHub app via @rehub/commerce + the single Supabase project.
import { openBillingPortal as openBillingPortalShared } from '@rehub/commerce';
export const openBillingPortal = (returnUrl?: string) =>
  openBillingPortalShared(supabase, returnUrl);

export async function navigateWithSSO(targetUrl: string, e?: { preventDefault: () => void }): Promise<void> {
  if (e) e.preventDefault();
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (session?.access_token && session?.refresh_token) {
      window.open(
        `${targetUrl}#sso_access_token=${session.access_token}&sso_refresh_token=${session.refresh_token}`,
        '_blank',
        'noopener,noreferrer'
      );
      return;
    }
  } catch {
    // Fall through to plain navigation
  }
  window.open(targetUrl, '_blank', 'noopener,noreferrer');
}

export async function fetchUserCredits(userId: string): Promise<number> {
  const { data, error } = await supabase
    .from('profiles')
    .select('credits')
    .eq('id', userId)
    .single();
  if (error) throw error;
  return data?.credits ?? 0;
}

export async function fetchSubscriptionTier(userId: string): Promise<string> {
  const { data, error } = await supabase
    .from('profiles')
    .select('tours_subscription_tier')
    .eq('id', userId)
    .single();
  if (error) return 'free';
  return (data as any)?.tours_subscription_tier ?? 'free';
}

export async function uploadShowcaseAsset(
  file: File,
  folder: 'images' | 'logos' | 'documents' | 'floorplans',
  userId: string
): Promise<string> {
  const ext = file.name.split('.').pop() || 'bin';
  const path = `${folder}/${userId}/${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`;

  const { data, error } = await supabase.storage
    .from('showcase-assets')
    .upload(path, file, { cacheControl: '3600', upsert: false });

  if (error) throw error;

  const { data: signedData, error: signError } = await supabase.storage
    .from('showcase-assets')
    .createSignedUrl(data.path, 60 * 60 * 24 * 365);

  if (signError || !signedData?.signedUrl) {
    throw signError ?? new Error('Failed to create signed URL');
  }

  return signedData.signedUrl;
}

const BRANDING_KEY = 'showcase_default_branding';

export interface DefaultBranding {
  logo_url: string;
  primary_brand_colour: string;
  contact_name: string;
  contact_email: string;
  contact_phone: string;
  office_name: string;
  office_address: string;
}

export function saveDefaultBranding(branding: DefaultBranding): void {
  localStorage.setItem(BRANDING_KEY, JSON.stringify(branding));
}

export function loadDefaultBranding(): DefaultBranding {
  try {
    const stored = localStorage.getItem(BRANDING_KEY);
    if (stored) return JSON.parse(stored);
  } catch {}
  return {
    logo_url: '',
    primary_brand_colour: '#2563eb',
    contact_name: '',
    contact_email: '',
    contact_phone: '',
    office_name: '',
    office_address: '',
  };
}

/**
 * Load the active sub-brand kit (or per-showcase override) and turn it
 * into BrandingForm-shaped overrides + a list of locked fields the editor
 * should disable. `subBrandId` lets a published showcase override the
 * user's active selection.
 */
export async function loadSubBrandForShowcase(
  userId: string,
  subBrandId?: string | null
): Promise<{
  brand: SubBrand | null;
  overrides: Partial<DefaultBranding>;
  lockedFields: BrandFieldKey[];
}> {
  const brand = subBrandId
    ? await getSubBrand(supabase, subBrandId)
    : await getActiveSubBrand(supabase, userId);
  if (!brand) return { brand: null, overrides: {}, lockedFields: [] };
  return {
    brand,
    overrides: {
      logo_url: brand.logo_url || '',
      primary_brand_colour: brand.primary_color,
      office_name: brand.agency_name || '',
    },
    lockedFields: brand.locked_fields,
  };
}

/**
 * @deprecated Use `loadSubBrandForShowcase`. Back-compat shim that maps
 * the new sub-brand API onto the old `{ locked, overrides }` shape.
 * `locked` is now true only when the sub-brand has *all* relevant fields
 * locked; UIs should switch to `lockedFields` for per-field gating.
 */
export async function loadTeamBrandingOverrides(userId: string): Promise<{
  locked: boolean;
  overrides: Partial<DefaultBranding>;
  lockedFields: BrandFieldKey[];
  brand: SubBrand | null;
}> {
  const { brand, overrides, lockedFields } = await loadSubBrandForShowcase(userId);
  if (!brand) return { locked: false, overrides: {}, lockedFields: [], brand: null };
  const locked = ['logo_url', 'primary_color', 'agency_name'].every(f =>
    lockedFields.includes(f as BrandFieldKey)
  );
  return { locked, overrides, lockedFields, brand };
}

/**
 * List the sub-brands the current user can pick from for a per-showcase override.
 * Returns an empty list (and a hidden picker) when only one is available.
 */
export async function listAvailableSubBrands(): Promise<SubBrand[]> {
  return listMySubBrands(supabase);
}

export { isFieldLocked };
export type { SubBrand, BrandFieldKey };
